import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Info } from "lucide-react";
import Select, { SingleValue } from "react-select";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import { selectSelectedInventoryItems } from "@src/store/pricing/queryEventInventoryAllSlice";
import { pricingActions } from "@src/store";
import { deleteSchedulePrice } from "@src/store/pricing/deleteSchedulePrice";
import { OptionType, filtersConfig } from "@src/components/utils/reactSelectUtils";
import { Button, ButtonVariant } from "../button/Button";
import { MyTooltip } from "../tooltip/Tooltip";
import { clx } from "../utils/stringUtils";
import EventContextHeader from "@src/components/eventContextHeader/EventContextHeader";
import BulkSchedulePriceItem from "./BulkSchedulePriceItem";
import styles from "./BulkSchedulePrice.module.scss";

type TBulkSchedulePrice = {
	handleClose?: () => void;
};

const CYCLE_OPTIONS = [
	{ label: "15 Minutes", value: "15" },
	{ label: "30 Minutes", value: "30" },
	{ label: "1 Hour", value: "60" },
	{ label: "2 Hours", value: "120" },
	{ label: "6 Hours", value: "360" },
	{ label: "1 Day", value: "1440" },
	{ label: "2 Days", value: "2880" },
	{ label: "7 Days", value: "10080" },
];

const TIME_OPTIONS: Array<OptionType> = (() => {
	const arr: Array<OptionType> = [];
	for (let h = 0; h < 24; h++) {
		for (let m = 0; m < 60; m += 15) {
			const abbr = h >= 12 ? "PM" : "AM";
			const h12 = h % 12 || 12;
			arr.push({
				value: `${String(h12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${abbr}`,
				label: `${h12}:${String(m).padStart(2, "0")} ${abbr}`,
			});
		}
	}
	return arr;
})();

const buildDateTime = (dateStr: string, timeStr: string): string => {
	const [year, month, day] = dateStr.split("-");
	const [hm, abbr] = timeStr.split(" ");
	const [hh, mm] = hm.split(":");
	return `${month}/${day}/${year} ${hh}:${mm}:00 ${abbr}`;
};

const niceDateTime = (dateStr: string, timeStr: string): string => {
	if (!dateStr) return "";
	const [year, month, day] = dateStr.split("-");
	const nice = timeStr ? timeStr.replace(/^0/, "") : "";
	return `${month}/${day}/${year}${nice ? ` ${nice}` : ""}`;
};

const toJsDate = (dateStr: string, timeStr: string): Date => {
	const [year, month, day] = dateStr.split("-").map(Number);
	const [hm, abbr] = timeStr.split(" ");
	let [hh] = hm.split(":").map(Number);
	const [, mm] = hm.split(":").map(Number);
	if (abbr === "PM" && hh < 12) hh += 12;
	if (abbr === "AM" && hh === 12) hh = 0;
	return new Date(year, month - 1, day, hh, mm, 0, 0);
};

type TAlterUnit = "$" | "%";

const getPriceChanges = (sDate: string, sTime: string, eDate: string, eTime: string, cycle: string): number => {
	if (!sDate || !eDate || !sTime || !eTime || !cycle) return 0;
	const diff = Math.floor((toJsDate(eDate, eTime).getTime() - toJsDate(sDate, sTime).getTime()) / 60000);
	if (diff <= 0) return 0;
	const changes = Math.floor(diff / Number(cycle));
	return changes > 0 ? changes : 0;
};

const computeAlterBy = (start: number, end: number, changes: number, unit: TAlterUnit): string => {
	if (!changes || isNaN(start) || isNaN(end)) return "";
	const perCycle = (end - start) / changes;
	if (!isFinite(perCycle)) return "";
	if (unit === "%") return start ? ((perCycle / start) * 100).toFixed(2) : "";
	return perCycle.toFixed(2);
};

const computeEndPrice = (start: number, alter: number, changes: number, unit: TAlterUnit): string => {
	if (!changes || isNaN(start) || isNaN(alter)) return "";
	const perCycle = unit === "%" ? start * (alter / 100) : alter;
	const end = start + perCycle * changes;
	return isFinite(end) ? end.toFixed(2) : "";
};

const toPerCycleDollar = (start: number, alter: number, unit: TAlterUnit): number =>
	unit === "%" ? start * (alter / 100) : alter;

const BulkSchedulePrice = ({ handleClose }: TBulkSchedulePrice) => {
	const dispatch = useAppDispatch();

	const items = useAppSelector(selectSelectedInventoryItems);
	const theme = useAppSelector((state) => state.settings.theme);
	const scheduleLoading = useAppSelector((state) => state.pricing.schedulePrice.loading);
	const deleteLoading = useAppSelector((state) => state.pricing.deleteSchedulePrice.loading);

	const [removedIds, setRemovedIds] = useState<Array<number>>([]);
	const workingItems = useMemo(() => items.filter((x) => !removedIds.includes(x.Listing_ID)), [items, removedIds]);

	const [startPrice, setStartPrice] = useState("");
	const [endPrice, setEndPrice] = useState("");
	const [alterPriceBy, setAlterPriceBy] = useState("");
	const [alterUnit, setAlterUnit] = useState<TAlterUnit>("$");
	const [cycle, setCycle] = useState("15");
	const [startDate, setStartDate] = useState("");
	const [startTime, setStartTime] = useState("09:00 AM");
	const [endDate, setEndDate] = useState("");
	const [endTime, setEndTime] = useState("09:00 PM");

	const submitting = scheduleLoading || deleteLoading;

	const handleRemoveItem = (listingId: number) => {
		setRemovedIds((prev) => (prev.includes(listingId) ? prev : [...prev, listingId]));
	};

	const parsedStart = parseFloat(startPrice);
	const parsedEnd = parseFloat(endPrice);

	const startDt = startDate && startTime ? toJsDate(startDate, startTime) : null;
	const endDt = endDate && endTime ? toJsDate(endDate, endTime) : null;
	const datesValid = !!startDt && !!endDt && endDt.getTime() > startDt.getTime();

	// Dates + start price are fixed anchors. Editing Alter Price By recomputes End price; editing any
	// other input (start/end price, cycle, dates) recomputes Alter Price By from the price gap.
	const syncAlterFromPrices = (s: string, e: string, sd: string, st: string, ed: string, et: string, cyc: string, unit: TAlterUnit) => {
		const changes = getPriceChanges(sd, st, ed, et, cyc);
		const nextAlter = computeAlterBy(parseFloat(s), parseFloat(e), changes, unit);
		if (nextAlter !== "") setAlterPriceBy(nextAlter);
	};

	const handleStartPriceChange = (v: string) => {
		setStartPrice(v);
		syncAlterFromPrices(v, endPrice, startDate, startTime, endDate, endTime, cycle, alterUnit);
	};
	const handleEndPriceChange = (v: string) => {
		setEndPrice(v);
		syncAlterFromPrices(startPrice, v, startDate, startTime, endDate, endTime, cycle, alterUnit);
	};
	const handleAlterChange = (v: string) => {
		setAlterPriceBy(v);
		const changes = getPriceChanges(startDate, startTime, endDate, endTime, cycle);
		const nextEnd = computeEndPrice(parseFloat(startPrice), parseFloat(v), changes, alterUnit);
		if (nextEnd !== "") setEndPrice(nextEnd);
	};
	const handleUnitChange = (u: TAlterUnit) => {
		setAlterUnit(u);
		syncAlterFromPrices(startPrice, endPrice, startDate, startTime, endDate, endTime, cycle, u);
	};
	const handleCycleChange = (v: string) => {
		setCycle(v);
		syncAlterFromPrices(startPrice, endPrice, startDate, startTime, endDate, endTime, v, alterUnit);
	};
	const handleStartDateChange = (v: string) => {
		setStartDate(v);
		syncAlterFromPrices(startPrice, endPrice, v, startTime, endDate, endTime, cycle, alterUnit);
	};
	const handleStartTimeChange = (v: string) => {
		setStartTime(v);
		syncAlterFromPrices(startPrice, endPrice, startDate, v, endDate, endTime, cycle, alterUnit);
	};
	const handleEndDateChange = (v: string) => {
		setEndDate(v);
		syncAlterFromPrices(startPrice, endPrice, startDate, startTime, v, endTime, cycle, alterUnit);
	};
	const handleEndTimeChange = (v: string) => {
		setEndTime(v);
		syncAlterFromPrices(startPrice, endPrice, startDate, startTime, endDate, v, cycle, alterUnit);
	};

	const canSchedule =
		workingItems.length > 0 &&
		!isNaN(parsedStart) &&
		!isNaN(parsedEnd) &&
		datesValid &&
		!!cycle &&
		alterPriceBy.trim() !== "" &&
		!isNaN(parseFloat(alterPriceBy)) &&
		!submitting;

	const handleSchedule = async () => {
		if (!canSchedule) return;
		const listingIds = workingItems.map((x) => x.Listing_ID);
		try {
			await dispatch(
				pricingActions.updateSchedulePrice({
					alterAmount: toPerCycleDollar(parsedStart, parseFloat(alterPriceBy) || 0, alterUnit) || 0,
					blackoutEnd: null,
					blackoutStart: null,
					endDateTime: buildDateTime(endDate, endTime),
					endPrice: parsedEnd,
					listingIds,
					priceCycleMinutes: Number(cycle),
					startDateTime: buildDateTime(startDate, startTime),
					startPrice: parsedStart,
				}),
			).unwrap();
			dispatch(pricingActions.updateListingPrices(listingIds.map((id) => ({ listing_id: id, price: parsedStart, face: null }))));
			toast.success("Scheduled pricing applied.");
			handleClose?.();
		} catch {
			toast.error("Error while scheduling pricing.");
		}
	};

	const handleDelete = async () => {
		if (!workingItems.length) return;
		try {
			await dispatch(deleteSchedulePrice({ ListingIds: workingItems.map((x) => x.Listing_ID) })).unwrap();
			toast.success("Scheduled pricing removed.");
			handleClose?.();
		} catch {
			toast.error("Failed to remove scheduled pricing.");
		}
	};

	const dateFrom = niceDateTime(startDate, startTime);
	const dateTo = niceDateTime(endDate, endTime);

	const mappedItems = workingItems.map((x) => (
		<BulkSchedulePriceItem
			key={x.Item_ID}
			data={x}
			startPrice={startPrice}
			endPrice={endPrice}
			dateFrom={dateFrom}
			dateTo={dateTo}
			onRemove={handleRemoveItem}
		/>
	));

	return (
		<div className={styles.root}>
			<EventContextHeader
				padding
				listings={workingItems.map((x) => ({ section: x.Section, row: x.Row, seats: x.Seats }))}
			/>
			<div className={styles.top}>
				<div className={styles.left}>
					<div className={clx(styles.subHeader, styles.mb)}>Pricing Rules</div>

					<div className={styles.twoCol}>
						<div className={styles.field}>
							<div className={styles.fieldLabel}>Starting price</div>
							<div className={styles.priceGroup}>
								<select className={styles.currencySelect} value="USD" onChange={() => void 0}>
									<option value="USD">$</option>
								</select>
								<input
									className={styles.priceInput}
									type="number"
									min={0}
									step="0.01"
									placeholder="0.00"
									value={startPrice}
									onChange={(e) => handleStartPriceChange(e.target.value)}
								/>
							</div>
						</div>
						<div className={styles.field}>
							<div className={styles.fieldLabel}>End price</div>
							<div className={styles.priceGroup}>
								<select className={styles.currencySelect} value="USD" onChange={() => void 0}>
									<option value="USD">$</option>
								</select>
								<input
									className={styles.priceInput}
									type="number"
									min={0}
									step="0.01"
									placeholder="0.00"
									value={endPrice}
									onChange={(e) => handleEndPriceChange(e.target.value)}
								/>
							</div>
						</div>
					</div>

					<div className={styles.twoCol}>
						<div className={styles.field}>
							<div className={styles.fieldLabel}>Cycle</div>
							<div className={styles.cycleGroup}>
								<span className={styles.infoPrefix}>
									<MyTooltip id="cycleInfo" popup="Cycle" popupStyle={{ fontSize: "1rem" }} element={<Info size={14} />} />
								</span>
								<select className={styles.cycleSelect} value={cycle} onChange={(e) => handleCycleChange(e.target.value)}>
									{CYCLE_OPTIONS.map((o) => (
										<option key={o.value} value={o.value}>
											{o.label}
										</option>
									))}
								</select>
							</div>
						</div>
						<div className={styles.field}>
							<div className={styles.fieldLabel}>Alter Price By</div>
							<div className={styles.cycleGroup}>
								<span className={styles.infoPrefix}>
									<MyTooltip
										id="alterInfo"
										popup="Editing this recalculates End price. Editing prices, cycle, or dates recalculates this."
										popupStyle={{ fontSize: "1rem" }}
										element={<Info size={14} />}
									/>
								</span>
								<select
									className={styles.unitSelect}
									value={alterUnit}
									onChange={(e) => handleUnitChange(e.target.value as TAlterUnit)}>
									<option value="$">$</option>
									<option value="%">%</option>
								</select>
								<input
									className={styles.cycleSelect}
									type="number"
									step="1"
									placeholder="0.00"
									value={alterPriceBy}
									onChange={(e) => handleAlterChange(e.target.value)}
								/>
							</div>
						</div>
					</div>

					<div className={styles.twoCol}>
						<div className={styles.field}>
							<div className={styles.fieldLabel}>Start Date</div>
							<input
								className={styles.input}
								type="date"
								value={startDate}
								onChange={(e) => handleStartDateChange(e.target.value)}
							/>
						</div>
						<div className={styles.field}>
							<div className={styles.fieldLabel}>Start Time (EST)</div>
							<Select<OptionType, false>
								options={TIME_OPTIONS}
								value={TIME_OPTIONS.find((o) => o.value === startTime) ?? null}
								isSearchable
								styles={filtersConfig("", theme)}
								onChange={(opt: SingleValue<OptionType>) => handleStartTimeChange(opt ? String(opt.value) : "")}
							/>
						</div>
					</div>

					<div className={styles.twoCol}>
						<div className={styles.field}>
							<div className={styles.fieldLabel}>End Date</div>
							<input
								className={styles.input}
								type="date"
								value={endDate}
								onChange={(e) => handleEndDateChange(e.target.value)}
							/>
						</div>
						<div className={styles.field}>
							<div className={styles.fieldLabel}>End Time (EST)</div>
							<Select<OptionType, false>
								options={TIME_OPTIONS}
								value={TIME_OPTIONS.find((o) => o.value === endTime) ?? null}
								isSearchable
								styles={filtersConfig("", theme)}
								onChange={(opt: SingleValue<OptionType>) => handleEndTimeChange(opt ? String(opt.value) : "")}
							/>
						</div>
					</div>

					{startDt && endDt && !datesValid ? (
						<div className={styles.dateError}>End date &amp; time must be after start date &amp; time.</div>
					) : null}

					{workingItems.length > 1 ? (
						<div className={styles.warning}>
							<div className={styles.warningHeader}>Warning! You have multiple listings selected.</div>
							<div className={styles.warningText}>
								Saving will overwrite any previous Scheduled Pricing settings saved to these listings.
							</div>
						</div>
					) : null}
				</div>

				<div className={styles.right}>
					<div className={styles.rightTopHeaders}>
						<div className={styles.subHeader}>Changes Summary</div>
						<div className={styles.subHeaderSecondary}>Applying to {workingItems.length} items</div>
					</div>
					<div data-no-border="true" className={styles.singleListing}>
						<div className={clx(styles.eventCol, styles.headerCol)}>Sec, Row, Seat</div>
						<div className={styles.qty}>Qty</div>
						<div className={styles.priceCol}>Current</div>
						<div className={styles.priceCol}>Start Price</div>
						<div className={styles.priceCol}>End Price</div>
						<div className={styles.xContainer}></div>
					</div>
					<div className={styles.listings}>{mappedItems}</div>
				</div>
			</div>
			<div className={styles.buttons}>
				<Button onClick={handleClose} variant={ButtonVariant.Secondary} disabled={submitting}>
					Cancel
				</Button>
				<Button
					onClick={handleDelete}
					variant={ButtonVariant.Secondary}
					isLoading={deleteLoading}
					disabled={!workingItems.length || submitting}
					className={styles.deleteButton}>
					Delete schedule
				</Button>
				<Button onClick={handleSchedule} isLoading={scheduleLoading} disabled={!canSchedule}>
					Schedule pricing
				</Button>
			</div>
		</div>
	);
};

export default BulkSchedulePrice;
